import {
  Table,
  Button,
  Row,
  Col,
  Form,
  Pagination,
  Card,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import Message from "./../components/Message";
import Loader from "./../components/Loader";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useGetSuppliesQuery, useDeleteSupplyMutation } from "./../slices/suppliesApiSlice";
import { toast } from "react-toastify";
import { useState } from "react";

const StaffSupply = () => {
  // Auto-load this month's supplies
  const today = new Date().toISOString().slice(0, 10);
  const firstDay = new Date();
  firstDay.setDate(1);

  const [filter, setFilter] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [pageNumber, setPageNumber] = useState(1);

  const [startDate, setStartDate] = useState(firstDay.toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(today);

  const [showFilters, setShowFilters] = useState(false);

  const { data, isLoading, error, refetch } = useGetSuppliesQuery(pageNumber);
  const [deleteSupply, { isLoading: loadingDelete }] = useDeleteSupplyMutation();

  // Quick date filters
  const setToday = () => {
    setStartDate(today);
    setEndDate(today);
  };

  const setThisWeek = () => {
    const now = new Date();
    const firstDayOfWeek = new Date(now.setDate(now.getDate() - now.getDay()));
    setStartDate(firstDayOfWeek.toISOString().slice(0, 10));
    setEndDate(today);
  };

  const setThisMonth = () => {
    setStartDate(firstDay.toISOString().slice(0, 10));
    setEndDate(today);
  };

  // Filtering logic
  const filterSupplies = () => {
    if (!data?.supplies) return [];

    let result = data.supplies.filter((supply) => {
      const d = new Date(supply.date);
      return (
        (!startDate || d >= new Date(startDate)) &&
        (!endDate || d <= new Date(endDate))
      );
    });

    if (filter.trim()) {
      const lf = filter.toLowerCase();
      result = result.filter((supply) => {
        return (
          supply.note?.toLowerCase().includes(lf) ||
          supply.username?.toLowerCase().includes(lf) ||
          supply.supplierDetails?.name?.toLowerCase().includes(lf) ||
          supply.supplyItems.some((it) => it.name.toLowerCase().includes(lf))
        );
      });
    }

    if (productSearch.trim()) {
      const lp = productSearch.toLowerCase();
      result = result
        .map((supply) => ({
          ...supply,
          supplyItems: supply.supplyItems.filter((it) =>
            it.name.toLowerCase().includes(lp)
          ),
        }))
        .filter((supply) => supply.supplyItems.length > 0);
    }

    return result;
  };

  // Totals
  const calculateTotal = (fn) =>
    filterSupplies().reduce(
      (total, supply) =>
        total + supply.supplyItems.reduce((sub, item) => sub + fn(item), 0),
      0
    );

  const totalCost = calculateTotal((i) => i.itemCost);
  const totalQty = calculateTotal((i) => i.qty);
  const totalRevenue = calculateTotal((i) => i.qty * i.price);
  const totalProfit = calculateTotal((i) => i.profit);

  // Excel export
  const exportToExcel = () => {
    const supplies = filterSupplies();
    const rows = supplies.flatMap((supply) =>
      supply.supplyItems.map((item) => ({
        Date: new Date(supply.date).toLocaleDateString(),
        Product: item.name,
        Quantity: item.qty,
        Revenue: item.qty * item.price,
        Cost: item.itemCost,
        Profit: item.profit,
        Note: supply.note || "",
        Supplier: supply.supplierDetails?.name || "",
      }))
    );

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Supplies");

    const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buffer], { type: "application/octet-stream" }), "Staff_Supplies.xlsx");
  };

  // Delete handler
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this supply?")) {
      try {
        await deleteSupply(id).unwrap();
        toast.success("Supply deleted successfully!");
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || "Failed to delete supply.");
      }
    }
  };

  return (
    <>
      {/* Header */}
      <Row className="align-items-center mb-4">
        <Col><h1>Supplies</h1></Col>
        <Col className="text-end">
          <Button variant="success" onClick={exportToExcel}>
            Export to Excel
          </Button>
        </Col>
      </Row>

      {/* Quick Buttons */}
      <Row className="mb-3">
        <Col>
          <Button variant="secondary" className="me-2" onClick={setToday}>Today</Button>
          <Button variant="secondary" className="me-2" onClick={setThisWeek}>This Week</Button>
          <Button variant="secondary" onClick={setThisMonth}>This Month</Button>
        </Col>

        <Col className="text-end">
          <Button
            variant="outline-dark"
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? "Hide Filters" : "Show Filters"}
          </Button>
        </Col>
      </Row>

      {/* Collapsible Filter Panel */}
      {showFilters && (
        <Card className="p-3 mb-4">
          <Row className="mb-3">
            <Col md={3}>
              <Form.Control
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </Col>

            <Col md={3}>
              <Form.Control
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </Col>

            <Col md={3}>
              <Form.Control
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Search by supplier, note, product"
              />
            </Col>

            <Col md={3}>
              <Form.Control
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                placeholder="Product only"
              />
            </Col>
          </Row>
        </Card>
      )}

      {/* Colored Summary Cards */}
      <Row className="mb-4" style={{ display: 'none' }}>
        <Col md={3}>
          <Card className="p-3 text-white" style={{ background: "#007bff" }}>
            <h5>Total Revenue</h5>
            <h3>${totalRevenue.toFixed(2)}</h3>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="p-3 text-white" style={{ background: "#28a745" }}>
            <h5>Total Cost</h5>
            <h3>${totalCost.toFixed(2)}</h3>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="p-3 text-white" style={{ background: "#ffc107" }}>
            <h5>Total Qty</h5>
            <h3>{totalQty.toFixed(2)}</h3>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="p-3 text-white" style={{ background: "#dc3545" }}>
            <h5>Total Profit</h5>
            <h3>${totalProfit.toFixed(2)}</h3>
          </Card>
        </Col>
      </Row>

      {loadingDelete && <Loader />}

      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">
          {error?.data?.message || "Something went wrong."}
        </Message>
      ) : (
        <>
          {/* Table */}
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th>DATE</th>
                <th>PRODUCT</th>
                <th>QTY</th>
                <th>COST</th>
                <th>PROFIT</th>
                <th>SUPPLYID</th>
                <th>SUPPLIER</th>
                <th>IMAGE</th>
                <th>ACTIONS</th>
              </tr>
            </thead>

            <tbody>
              {filterSupplies().map((supply) =>
                supply.supplyItems.map((item) => (
                  <tr key={`${supply._id}-${item.name}`}>
                    <td>{new Date(supply.date).toLocaleDateString()}</td>
                    <td>{item.name}</td>
                    <td>{item.qty}</td>
                    <td>${item.itemCost}</td>
                    <td>${item.profit.toFixed(2)}</td>
                    <td>{supply.note || "N/A"}</td>
                    <td>{supply.supplierDetails?.name || "N/A"}</td>

                    <td>
                      {item.images?.[0] && (
                        <img
                          src={item.images[0]}
                          alt="Product"
                          style={{
                            width: "10px",
                            height: "10px",
                            objectFit: "cover",
                            borderRadius: "5px",
                          }}
                        />
                      )}
                    </td>

                    <td>
                      <Button
                        as={Link}
                        to={`/supply/${supply._id}`}
                        variant="light"
                        className="btn-sm"
                      >
                        Details
                      </Button>
                      <Button
                          style={{ display: 'none' }}
                          variant="danger"
                          size="sm"
                            className="ms-2"
                            onClick={() => handleDelete(supply._id)}
                        >
                         Delete
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          {/* Pagination */}
          <Pagination>
            {[...Array(data.pages).keys()].map((x) => (
              <Pagination.Item
                key={x + 1}
                active={x + 1 === pageNumber}
                onClick={() => setPageNumber(x + 1)}
              >
                {x + 1}
              </Pagination.Item>
            ))}
          </Pagination>
        </>
      )}
    </>
  );
};

export default StaffSupply;
